import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import './BookingChannelPage.css';
import '../HotelPricing/HotelPricing.css';
import { useTranslation } from 'react-i18next';

type ChannelAccount = {
  id: number;
  companyId: number;
  integrationId: number;
  name: string;
  isActive: boolean;
  created: string;
};

type ChannelPropertyMap = {
  id: number;
  channelAccountId: number;
  hotelId: number;
  externalHotelId: string;
  created: string;
};

type ChannelRoomTypeMap = {
  id: number;
  channelAccountId: number;
  roomTypeId: number;
  hotelId?: number;
  roomTypeName?: string;
  roomTypeCode?: string;
  externalRoomTypeId: string;
  created?: string | null;
};

type ChannelRatePlanMap = {
  id: number;
  channelAccountId: number;
  ratePlanId: number;
  hotelId?: number;
  ratePlanName?: string;
  ratePlanCode?: string;
  externalRatePlanId: string;
  created?: string | null;
};

type ChannelReservationMap = {
  id: number;
  channelAccountId: number;
  externalReservationId: string;
  reservationId: number;
  status: string;
  lastSyncAt?: string | null;
  created: string;
};

type ChannelSyncJob = {
  id: number;
  channelAccountId: number;
  jobType: string;
  status: string;
  payload?: string | null;
  response?: string | null;
  retryCount: number;
  createdAt: string;
  processedAt?: string | null;
};

type ChannelSyncError = {
  id: number;
  channelAccountId: number;
  entityType: string;
  entityId: string;
  errorMessage: string;
  stackTrace?: string | null;
  createdAt: string;
};

type BookingChannelResponse = {
  channelAccounts?: ChannelAccount[];
  propertyMaps?: ChannelPropertyMap[];
  roomTypeMaps?: ChannelRoomTypeMap[];
  ratePlanMaps?: ChannelRatePlanMap[];
  reservationMaps?: ChannelReservationMap[];
  syncJobs?: ChannelSyncJob[];
  syncErrors?: ChannelSyncError[];
  ChannelAccounts?: ChannelAccount[];
  PropertyMaps?: ChannelPropertyMap[];
  RoomTypeMaps?: ChannelRoomTypeMap[];
  RatePlanMaps?: ChannelRatePlanMap[];
  ReservationMaps?: ChannelReservationMap[];
  SyncJobs?: ChannelSyncJob[];
  SyncErrors?: ChannelSyncError[];
};

type HotelOption = {
  id: number;
  name: string;
  code?: string;
};

type RoomTypeOption = {
  id: number;
  hotelId: number;
  name: string;
  code?: string;
};

type RatePlanOption = {
  id: number;
  hotelId: number;
  name: string;
  code?: string;
};

type ListGridResponse<T> = {
  items?: T[];
  Items?: T[];
};

type IntegrationOption = {
  integrationId: number;
  integrationCode: string;
  integrationName: string;
};

type IntegrationListItem = {
  id: number;
  code: string;
  name: string;
};

type IntegrationSettingDto = {
  id: number;
  integrationId: number;
  key: string;
  value: string | null;
  valueType: string;
  isSecret: boolean;
};

const HOTEL_CHANNEL_INTEGRATION_CODES = new Set(['BOOKING', 'AIRBNB']);

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const statusClassName = (value?: string) => (value ?? '').trim().toLowerCase().replace(/\s+/g, '-');
const bookingSettingKey = (channelAccountId: number, suffix: string) => `Booking:${channelAccountId}:${suffix}`;

type ChannelTab = 'accounts' | 'mappings' | 'reservations' | 'sync';
type MappingTab = 'property' | 'roomType' | 'ratePlan';
type SyncTab = 'jobs' | 'errors';

const BookingChannelPage: React.FC = () => {
  const { i18n } = useTranslation();
  const rowsLabel = (count: number) => i18n.t('bookingChannel.common.rows', { count, defaultValue: '{{count}} rows' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<ChannelTab>('accounts');
  const [activeMappingTab, setActiveMappingTab] = useState<MappingTab>('property');
  const [activeSyncTab, setActiveSyncTab] = useState<SyncTab>('jobs');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [showAddPropertyMap, setShowAddPropertyMap] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
  const [savingPropertyMap, setSavingPropertyMap] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [channelAccounts, setChannelAccounts] = useState<ChannelAccount[]>([]);
  const [propertyMaps, setPropertyMaps] = useState<ChannelPropertyMap[]>([]);
  const [roomTypeMaps, setRoomTypeMaps] = useState<ChannelRoomTypeMap[]>([]);
  const [ratePlanMaps, setRatePlanMaps] = useState<ChannelRatePlanMap[]>([]);
  const [reservationMaps, setReservationMaps] = useState<ChannelReservationMap[]>([]);
  const [syncJobs, setSyncJobs] = useState<ChannelSyncJob[]>([]);
  const [syncErrors, setSyncErrors] = useState<ChannelSyncError[]>([]);
  const [hotels, setHotels] = useState<HotelOption[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomTypeOption[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlanOption[]>([]);
  const [integrationOptions, setIntegrationOptions] = useState<IntegrationOption[]>([]);
  const [accountForm, setAccountForm] = useState({ integrationId: 0, name: '', isActive: true });
  const [propertyMapForm, setPropertyMapForm] = useState({ hotelId: 0, externalHotelId: '' });
  const [credentialsForm, setCredentialsForm] = useState({ username: '', password: '' });
  const [hasStoredPassword, setHasStoredPassword] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get<BookingChannelResponse>(ApiConstants.bookingChannel.overview);
        const data = response.data ?? {};

        const nextAccounts = data.channelAccounts ?? data.ChannelAccounts ?? [];
        setChannelAccounts(nextAccounts);
        setPropertyMaps(data.propertyMaps ?? data.PropertyMaps ?? []);
        setRoomTypeMaps(data.roomTypeMaps ?? data.RoomTypeMaps ?? []);
        setRatePlanMaps(data.ratePlanMaps ?? data.RatePlanMaps ?? []);
        setReservationMaps(data.reservationMaps ?? data.ReservationMaps ?? []);
        setSyncJobs(data.syncJobs ?? data.SyncJobs ?? []);
        setSyncErrors(data.syncErrors ?? data.SyncErrors ?? []);

        setSelectedAccountId((current) => {
          if (current > 0 && nextAccounts.some((item) => Number(item.id) === Number(current))) {
            return current;
          }

          return nextAccounts[0]?.id ?? 0;
        });
      } catch (err: any) {
        toast.error(err?.response?.data || 'Failed to load booking channel data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [hotelsResponse, integrationsResponse, roomTypesResponse, ratePlansResponse] = await Promise.all([
          axiosInstance.get<HotelOption[]>(ApiConstants.hotel.hotels),
          axiosInstance.get<IntegrationListItem[]>(ApiConstants.integrations.list),
          axiosInstance.get<RoomTypeOption[]>(ApiConstants.hotel.roomTypes),
          axiosInstance.post<ListGridResponse<RatePlanOption>>(ApiConstants.hotelPricing.ratePlanList, {
            gridItems: [],
            pagination: { page: 1, itemPerPage: 1000, count: 0 },
          }),
        ]);

        setHotels(hotelsResponse.data ?? []);
        setIntegrationOptions(
          (integrationsResponse.data ?? []).map((item) => ({
            integrationId: item.id,
            integrationCode: item.code,
            integrationName: item.name,
          })),
        );
        setRoomTypes(roomTypesResponse.data ?? []);
        setRatePlans(ratePlansResponse.data?.items ?? ratePlansResponse.data?.Items ?? []);
      } catch {
        toast.error('Failed to load hotels or integration options');
      }
    };

    loadOptions();
  }, []);

  const selectedAccount = useMemo(
    () => channelAccounts.find((item) => Number(item.id) === Number(selectedAccountId)) ?? null,
    [channelAccounts, selectedAccountId],
  );

  const query = searchTerm.trim().toLowerCase();

  const filterRows = <T extends Record<string, any>>(rows: T[], fields: Array<keyof T>) =>
    rows.filter((row) => {
      if (selectedAccountId > 0 && Number(row.channelAccountId) !== Number(selectedAccountId)) return false;
      if (!query) return true;
      return fields.some((field) => String(row[field] ?? '').toLowerCase().includes(query));
    });

  const filteredPropertyMaps = filterRows(propertyMaps, ['externalHotelId', 'hotelId']);

  const selectedPropertyHotelIds = useMemo(
    () =>
      propertyMaps
        .filter((item) => (selectedAccountId > 0 ? Number(item.channelAccountId) === Number(selectedAccountId) : true))
        .map((item) => item.hotelId),
    [propertyMaps, selectedAccountId],
  );

  const derivedRoomTypeRows = useMemo(() => {
    const hotelIdSet = new Set(selectedPropertyHotelIds);
    const mappedByRoomTypeId = new Map(
      roomTypeMaps
        .filter((item) => Number(item.channelAccountId) === Number(selectedAccountId))
        .map((item) => [item.roomTypeId, item]),
    );

    return roomTypes
      .filter((item) => hotelIdSet.has(item.hotelId))
      .map((item) => {
        const mapped = mappedByRoomTypeId.get(item.id);
        return {
          id: mapped?.id ?? 0,
          channelAccountId: selectedAccountId,
          roomTypeId: item.id,
          hotelId: item.hotelId,
          roomTypeName: item.name,
          roomTypeCode: item.code,
          externalRoomTypeId: mapped?.externalRoomTypeId ?? '',
          created: mapped?.created ?? null,
        } as ChannelRoomTypeMap;
      });
  }, [roomTypeMaps, roomTypes, selectedAccountId, selectedPropertyHotelIds]);

  const derivedRatePlanRows = useMemo(() => {
    const hotelIdSet = new Set(selectedPropertyHotelIds);
    const mappedByRatePlanId = new Map(
      ratePlanMaps
        .filter((item) => Number(item.channelAccountId) === Number(selectedAccountId))
        .map((item) => [item.ratePlanId, item]),
    );

    return ratePlans
      .filter((item) => hotelIdSet.has(item.hotelId))
      .map((item) => {
        const mapped = mappedByRatePlanId.get(item.id);
        return {
          id: mapped?.id ?? 0,
          channelAccountId: selectedAccountId,
          ratePlanId: item.id,
          hotelId: item.hotelId,
          ratePlanName: item.name,
          ratePlanCode: item.code,
          externalRatePlanId: mapped?.externalRatePlanId ?? '',
          created: mapped?.created ?? null,
        } as ChannelRatePlanMap;
      });
  }, [ratePlanMaps, ratePlans, selectedAccountId, selectedPropertyHotelIds]);

  const filteredRoomTypeMaps = filterRows(derivedRoomTypeRows, ['externalRoomTypeId', 'roomTypeId', 'roomTypeName', 'roomTypeCode', 'hotelId']);
  const filteredRatePlanMaps = filterRows(derivedRatePlanRows, ['externalRatePlanId', 'ratePlanId', 'ratePlanName', 'ratePlanCode', 'hotelId']);
  const filteredReservationMaps = filterRows(reservationMaps, ['externalReservationId', 'reservationId', 'status']);
  const filteredSyncJobs = filterRows(syncJobs, ['jobType', 'status', 'payload', 'response']);
  const filteredSyncErrors = filterRows(syncErrors, ['entityType', 'entityId', 'errorMessage']);

  const summary = useMemo(() => {
    const activeAccounts = channelAccounts.filter((item) => item.isActive).length;
    const pendingJobs = syncJobs.filter((item) => (item.status ?? '').toLowerCase() === 'pending').length;
    const failedJobs = syncJobs.filter((item) => (item.status ?? '').toLowerCase() === 'failed').length;
    const mappedReservations = reservationMaps.length;

    return { activeAccounts, pendingJobs, failedJobs, mappedReservations };
  }, [channelAccounts, reservationMaps.length, syncJobs]);

  const filteredIntegrationOptions = useMemo(
    () =>
      integrationOptions.filter((item) =>
        HOTEL_CHANNEL_INTEGRATION_CODES.has((item.integrationCode ?? '').trim().toUpperCase()),
      ),
    [integrationOptions],
  );

  useEffect(() => {
    const loadCredentials = async () => {
      if (!selectedAccount?.integrationId || !selectedAccount?.id) {
        setCredentialsForm({ username: '', password: '' });
        setHasStoredPassword(false);
        return;
      }

      try {
        setLoadingCredentials(true);
        const response = await axiosInstance.get<IntegrationSettingDto[]>(
          ApiConstants.integrationSettings.byIntegrationId(selectedAccount.integrationId),
        );
        const settings = response.data ?? [];
        const usernameKey = bookingSettingKey(selectedAccount.id, 'Username').toLowerCase();
        const passwordKey = bookingSettingKey(selectedAccount.id, 'Password').toLowerCase();
        const usernameSetting = settings.find((item) => item.key.trim().toLowerCase() === usernameKey);
        const passwordSetting = settings.find((item) => item.key.trim().toLowerCase() === passwordKey);

        setCredentialsForm({
          username: usernameSetting?.value ?? '',
          password: '',
        });
        setHasStoredPassword(Boolean(passwordSetting));
      } catch {
        setCredentialsForm({ username: '', password: '' });
        setHasStoredPassword(false);
        toast.error('Failed to load booking credentials');
      } finally {
        setLoadingCredentials(false);
      }
    };

    loadCredentials();
  }, [selectedAccount?.id, selectedAccount?.integrationId]);

  const reloadOverview = async () => {
    const response = await axiosInstance.get<BookingChannelResponse>(ApiConstants.bookingChannel.overview);
    const data = response.data ?? {};
    const nextAccounts = data.channelAccounts ?? data.ChannelAccounts ?? [];
    setChannelAccounts(nextAccounts);
    setPropertyMaps(data.propertyMaps ?? data.PropertyMaps ?? []);
    setRoomTypeMaps(data.roomTypeMaps ?? data.RoomTypeMaps ?? []);
    setRatePlanMaps(data.ratePlanMaps ?? data.RatePlanMaps ?? []);
    setReservationMaps(data.reservationMaps ?? data.ReservationMaps ?? []);
    setSyncJobs(data.syncJobs ?? data.SyncJobs ?? []);
    setSyncErrors(data.syncErrors ?? data.SyncErrors ?? []);
  };

  const openEditAccount = (account: ChannelAccount) => {
    setSelectedAccountId(account.id);
    setAccountForm({
      integrationId: account.integrationId,
      name: account.name,
      isActive: account.isActive,
    });
    setShowEditAccountModal(true);
  };

  const closeEditAccountModal = () => {
    setShowEditAccountModal(false);
    setAccountForm({ integrationId: 0, name: '', isActive: true });
  };

  const handleAddAccount = async () => {
    if (accountForm.integrationId <= 0) {
      toast.error('Zgjidh integrimin');
      return;
    }

    if (!accountForm.name.trim()) {
      toast.error('Shkruaj emrin e account');
      return;
    }

    try {
      setSavingAccount(true);
      const response = await axiosInstance.post<ChannelAccount>(ApiConstants.bookingChannel.addAccount, accountForm);
      await reloadOverview();
      if (response.data?.id) {
        setSelectedAccountId(response.data.id);
      }
      setAccountForm({ integrationId: 0, name: '', isActive: true });
      setShowAddAccount(false);
      toast.success('Channel account u shtua me sukses');
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to add channel account');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleAddPropertyMap = async () => {
    if (selectedAccountId <= 0) {
      toast.error('Zgjidh nje channel account');
      return;
    }

    if (propertyMapForm.hotelId <= 0) {
      toast.error('Zgjidh hotelin');
      return;
    }

    if (!propertyMapForm.externalHotelId.trim()) {
      toast.error('Shkruaj External Hotel ID');
      return;
    }

    try {
      setSavingPropertyMap(true); 
      await axiosInstance.post(ApiConstants.bookingChannel.addPropertyMap, {
        channelAccountId: selectedAccountId,
        hotelId: propertyMapForm.hotelId,
        externalHotelId: propertyMapForm.externalHotelId,
      });
      await reloadOverview();
      setPropertyMapForm({ hotelId: 0, externalHotelId: '' });
      setShowAddPropertyMap(false);
      toast.success('Hoteli u lidh me channel account');
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to add hotel to channel');
    } finally {
      setSavingPropertyMap(false);
    }
  };

  const handleToggleAccountActive = async (account: ChannelAccount) => {
    try {
      setEditingAccountId(account.id);
      await axiosInstance.put(ApiConstants.bookingChannel.updateAccount(account.id), {
        isActive: !account.isActive,
      });
      await reloadOverview();
      toast.success('Channel account u perditesua me sukses');
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to update channel account');
    } finally {
      setEditingAccountId(null);
    }
  };

  const handleSaveEditModal = async () => {
    if (!selectedAccount) {
      toast.error('Zgjidh nje channel account');
      return;
    }

    if (accountForm.integrationId <= 0) {
      toast.error('Zgjidh integrimin');
      return;
    }

    if (!accountForm.name.trim()) {
      toast.error('Shkruaj emrin e account');
      return;
    }

    if (!credentialsForm.username.trim()) {
      toast.error('Shkruaj Booking Username');
      return;
    }

    if (!hasStoredPassword && !credentialsForm.password.trim()) {
      toast.error('Shkruaj Booking Password');
      return;
    }

    try {
      setSavingAccount(true);
      setSavingCredentials(true);

      await axiosInstance.put(ApiConstants.bookingChannel.updateAccount(selectedAccount.id), {
        integrationId: accountForm.integrationId,
        name: accountForm.name.trim(),
        isActive: accountForm.isActive,
      });

      const payload = [
        {
          integrationId: accountForm.integrationId,
          key: bookingSettingKey(selectedAccount.id, 'Username'),
          value: credentialsForm.username.trim(),
          valueType: 'string',
          isSecret: false,
        },
      ];

      if (credentialsForm.password.trim()) {
        payload.push({
          integrationId: accountForm.integrationId,
          key: bookingSettingKey(selectedAccount.id, 'Password'),
          value: credentialsForm.password.trim(),
          valueType: 'string',
          isSecret: true,
        });
      }

      await axiosInstance.post(ApiConstants.integrationSettings.saveAll, payload);
      await reloadOverview();
      setCredentialsForm((current) => ({ ...current, password: '' }));
      setHasStoredPassword(true);
      closeEditAccountModal();
      toast.success('Channel account u perditesua me sukses');
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to save channel account');
    } finally {
      setSavingAccount(false);
      setSavingCredentials(false);
    }
  };

  const channelTabs = (
    <div className="hotelPricingTabs channelTabs">
      <button type="button" className={`hotelPricingTab ${activeTab === 'accounts' ? 'active' : ''}`} onClick={() => setActiveTab('accounts')}>
        {i18n.t('bookingChannel.tabs.accounts', { defaultValue: 'Channel Accounts' })}
      </button>
      <button type="button" className={`hotelPricingTab ${activeTab === 'mappings' ? 'active' : ''}`} onClick={() => setActiveTab('mappings')}>
        {i18n.t('bookingChannel.tabs.mappings', { defaultValue: 'Mappings' })}
      </button>
      <button
        type="button"
        className={`hotelPricingTab ${activeTab === 'reservations' ? 'active' : ''}`}
        onClick={() => setActiveTab('reservations')}
      >
        {i18n.t('bookingChannel.tabs.reservations', { defaultValue: 'Reservations' })}
      </button>
      <button type="button" className={`hotelPricingTab ${activeTab === 'sync' ? 'active' : ''}`} onClick={() => setActiveTab('sync')}>
        {i18n.t('bookingChannel.tabs.sync', { defaultValue: 'Sync & Errors' })}
      </button>
    </div>
  );

  const mappingTabs = (
    <div className="hotelPricingTabs channelTabs">
      <button
        type="button"
        className={`hotelPricingTab ${activeMappingTab === 'property' ? 'active' : ''}`}
        onClick={() => setActiveMappingTab('property')}
      >
          {i18n.t('bookingChannel.mappingTabs.property', { defaultValue: 'Property Maps' })}
      </button>
      <button
        type="button"
        className={`hotelPricingTab ${activeMappingTab === 'roomType' ? 'active' : ''}`}
        onClick={() => setActiveMappingTab('roomType')}
      >
          {i18n.t('bookingChannel.mappingTabs.roomType', { defaultValue: 'Room Type Maps' })}
      </button>
      <button
        type="button"
        className={`hotelPricingTab ${activeMappingTab === 'ratePlan' ? 'active' : ''}`}
        onClick={() => setActiveMappingTab('ratePlan')}
      >
          {i18n.t('bookingChannel.mappingTabs.ratePlan', { defaultValue: 'Rate Plan Maps' })}
      </button>
    </div>
  );

  const syncTabs = (
    <div className="hotelPricingTabs channelTabs">
      <button type="button" className={`hotelPricingTab ${activeSyncTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveSyncTab('jobs')}>
          {i18n.t('bookingChannel.syncTabs.jobs', { defaultValue: 'Sync Jobs' })}
      </button>
      <button type="button" className={`hotelPricingTab ${activeSyncTab === 'errors' ? 'active' : ''}`} onClick={() => setActiveSyncTab('errors')}>
          {i18n.t('bookingChannel.syncTabs.errors', { defaultValue: 'Sync Errors' })}
      </button>
    </div>
  );

  return (
    <BasicCli>
      <div className="channelPage">
        <section className="channelHero">
            <div className="channelHeroCopy">
              <span className="channelEyebrow">{i18n.t('bookingChannel.hero.kicker', { defaultValue: 'Hotel Distribution' })}</span>
              <h1>{i18n.t('menu.BookingChannel', { defaultValue: 'Booking Platform' })}</h1>
            {/* <p>
              Menaxho channel account-et, mappings, reservation sync queue dhe error logs me te dhena reale nga databaza.
            </p> */}
          </div>

          <div className="channelHeroActions">
            <label className="channelControl">
              <span>Channel Account</span>
              <select value={selectedAccountId} onChange={(e) => setSelectedAccountId(Number(e.target.value))} disabled={loading || channelAccounts.length === 0}>
                {channelAccounts.length === 0 ? (
                  <option value={0}>{i18n.t('bookingChannel.common.noChannelAccounts', { defaultValue: 'No channel accounts' })}</option>
                ) : (
                  channelAccounts.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))
                )}
              </select>
            </label>

            {/* <label className="channelControl channelControlWide">
              <span>Kerko ne tabela</span>
              <input
                type="text"
                value={searchTerm}
                placeholder="reservation id, external id, status..."
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </label> */}
          </div>
        </section>

        <section className="channelStats">
          <article className="channelStatCard">
             <span>{i18n.t('bookingChannel.stats.activeAccounts.title', { defaultValue: 'Active Accounts' })}</span>
            <strong>{summary.activeAccounts}</strong>
             <small>{i18n.t('bookingChannel.stats.activeAccounts.description', { defaultValue: 'Active channel accounts in the system' })}</small>
          </article>
          <article className="channelStatCard">
             <span>{i18n.t('bookingChannel.stats.pendingJobs.title', { defaultValue: 'Pending Jobs' })}</span>
            <strong>{summary.pendingJobs}</strong>
             <small>{i18n.t('bookingChannel.stats.pendingJobs.description', { defaultValue: 'Pending synchronizations' })}</small>
          </article>
          <article className="channelStatCard">
             <span>{i18n.t('bookingChannel.stats.failedJobs.title', { defaultValue: 'Failed Jobs' })}</span>
            <strong>{summary.failedJobs}</strong>
             <small>{i18n.t('bookingChannel.stats.failedJobs.description', { defaultValue: 'Items that need review' })}</small>
          </article>
          <article className="channelStatCard">
             <span>{i18n.t('bookingChannel.stats.reservationMaps.title', { defaultValue: 'Reservation Maps' })}</span>
            <strong>{summary.mappedReservations}</strong>
             <small>{i18n.t('bookingChannel.stats.reservationMaps.description', { defaultValue: 'Reservations linked with channels' })}</small>
          </article>
        </section>

        <section className="channelPanel channelTabsPanel">
          {channelTabs}
        </section>

        {activeTab === 'accounts' && (
        <section className="channelPanel">
            <div className="channelPanelHead">
              <div>
                  <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.account.kicker', { defaultValue: 'Account' })}</span>
                <h2>{i18n.t('bookingChannel.sections.account.title', { defaultValue: 'Channel Accounts' })}</h2>
              </div>
              <div className="channelPanelHeadActions">
                <button type="button" className="channelAddButton" onClick={() => setShowAddAccount((current) => !current)}>
                  {showAddAccount ? 'Close' : 'Add Account'}
                </button>
                <div className={`channelBadge ${selectedAccount?.isActive ? 'is-active' : 'is-inactive'}`}>
                  {selectedAccount
                    ? (selectedAccount.isActive
                      ? i18n.t('bookingChannel.common.active', { defaultValue: 'Active' })
                      : i18n.t('bookingChannel.common.inactive', { defaultValue: 'Inactive' }))
                    : i18n.t('bookingChannel.common.noAccount', { defaultValue: 'No account' })}
                </div>
              </div>
            </div>

            {showAddAccount && (
              <div className="channelFormCard">
                <div className="channelFormGrid">
                  <label className="channelControl">
                    <span>Integration</span>
                    <select
                      value={accountForm.integrationId}
                      onChange={(e) => setAccountForm((current) => ({ ...current, integrationId: Number(e.target.value) }))}
                    >
                      <option value={0}>Select integration</option>
                      {filteredIntegrationOptions.map((item) => (
                        <option key={item.integrationId} value={item.integrationId}>
                          {item.integrationName} ({item.integrationCode})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="channelControl">
                    <span>Account Name</span>
                    <input
                      type="text"
                      value={accountForm.name}
                      placeholder="Booking.com Main Account"
                      onChange={(e) => setAccountForm((current) => ({ ...current, name: e.target.value }))}
                    />
                  </label>

                  <label className="channelCheckbox">
                    <input
                      type="checkbox"
                      checked={accountForm.isActive}
                      onChange={(e) => setAccountForm((current) => ({ ...current, isActive: e.target.checked }))}
                    />
                    <span>Active</span>
                  </label>
                </div>

                <div className="channelFormActions">
                  <button
                    type="button"
                    className="channelPrimaryButton"
                    onClick={handleAddAccount}
                    disabled={savingAccount}
                  >
                    {savingAccount ? 'Saving...' : 'Save Account'}
                  </button>
                </div>
              </div>
            )}

            <div className="channelTableWrap">
              <table className="channelTable">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Integration</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {channelAccounts.map((item) => (
                    <tr
                      key={item.id}
                      className={item.id === selectedAccountId ? 'is-selected' : ''}
                      onClick={() => setSelectedAccountId(item.id)}
                    >
                      <td>{item.id}</td>
                      <td>{item.name}</td>
                      <td>{item.companyId}</td>
                      <td>{item.integrationId}</td>
                      <td>
                        <span className={`channelPill ${item.isActive ? 'active' : 'inactive'}`}>
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDateTime(item.created)}</td>
                      <td>
                        <button
                          type="button"
                          className="channelAddButton"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditAccount(item);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="channelAddButton"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleAccountActive(item);
                          }}
                          disabled={editingAccountId === item.id}
                        >
                          {editingAccountId === item.id ? 'Saving...' : item.isActive ? 'Set Inactive' : 'Set Active'}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && channelAccounts.length === 0 && (
                    <tr>
                      <td colSpan={7} className="channelEmptyCell">
                        {i18n.t('bookingChannel.empty.accounts', { defaultValue: 'No channel accounts found in the database.' })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {showEditAccountModal && selectedAccount && (
          <GeneralModal
            handleClose={closeEditAccountModal}
            headerName={`Edit Channel Account #${selectedAccount.id}`}
            width="900px"
            radius="16px"
            hideFooter
          >
            <div className="channelFormCard">
              <div className="channelFormGrid">
                <label className="channelControl">
                  <span>Integration</span>
                  <select
                    value={accountForm.integrationId}
                    onChange={(e) => setAccountForm((current) => ({ ...current, integrationId: Number(e.target.value) }))}
                  >
                    <option value={0}>Select integration</option>
                    {filteredIntegrationOptions.map((item) => (
                      <option key={item.integrationId} value={item.integrationId}>
                        {item.integrationName} ({item.integrationCode})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="channelControl">
                  <span>Account Name</span>
                  <input
                    type="text"
                    value={accountForm.name}
                    placeholder="Booking.com Main Account"
                    onChange={(e) => setAccountForm((current) => ({ ...current, name: e.target.value }))}
                  />
                </label>

                <label className="channelCheckbox">
                  <input
                    type="checkbox"
                    checked={accountForm.isActive}
                    onChange={(e) => setAccountForm((current) => ({ ...current, isActive: e.target.checked }))}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="channelPanelHead">
                <div>
                  <span className="channelPanelKicker">Credentials</span>
                  <h2>Booking API Credentials</h2>
                </div>
                <div className={`channelBadge ${hasStoredPassword ? 'is-active' : ''}`}>
                  {loadingCredentials ? 'Loading...' : hasStoredPassword ? 'Password saved' : 'Password missing'}
                </div>
              </div>

              <div className="channelFormGrid">
                <label className="channelControl">
                  <span>Booking Username</span>
                  <input
                    type="text"
                    value={credentialsForm.username}
                    placeholder={`Booking:${selectedAccount.id}:Username`}
                    onChange={(e) => setCredentialsForm((current) => ({ ...current, username: e.target.value }))}
                    disabled={loadingCredentials}
                  />
                </label>

                <label className="channelControl">
                  <span>Booking Password</span>
                  <input
                    type="password"
                    value={credentialsForm.password}
                    placeholder={hasStoredPassword ? 'Password already saved. Enter only to replace it.' : `Booking:${selectedAccount.id}:Password`}
                    onChange={(e) => setCredentialsForm((current) => ({ ...current, password: e.target.value }))}
                    disabled={loadingCredentials}
                  />
                </label>
              </div>

              <div className="channelFormActions">
                <button type="button" className="channelAddButton channelActionGhost" onClick={closeEditAccountModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="channelPrimaryButton"
                  onClick={handleSaveEditModal}
                  disabled={loadingCredentials || savingAccount || savingCredentials}
                >
                  {savingAccount || savingCredentials ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </GeneralModal>
        )}

        {activeTab === 'mappings' && (
        <section className="channelPanel">
            <div className="channelPanelHead">
              <div>
                  <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.mapping.kicker', { defaultValue: 'Mapping' })}</span>
                  <h2>{i18n.t('bookingChannel.sections.mapping.title', { defaultValue: 'Mappings' })}</h2>
              </div>
            </div>
            {mappingTabs}

            {activeMappingTab === 'property' && (
            <div className="channelPanel">
              <div className="channelPanelHead">
                <div>
                    <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.mapping.kicker', { defaultValue: 'Mapping' })}</span>
                    <h2>{i18n.t('bookingChannel.mappingTabs.property', { defaultValue: 'Property Maps' })}</h2>
                </div>
                <div className="channelPanelHeadActions">
                  <button type="button" className="channelAddButton" onClick={() => setShowAddPropertyMap((current) => !current)}>
                    {showAddPropertyMap
                      ? i18n.t('bookingChannel.common.close', { defaultValue: 'Close' })
                      : i18n.t('bookingChannel.actions.addHotelToChannel', { defaultValue: 'Add Hotel to Channel' })}
                  </button>
                  <div className="channelBadge">{rowsLabel(filteredPropertyMaps.length)}</div>
                </div>
              </div>

              {showAddPropertyMap && (
                <div className="channelFormCard">
                  <div className="channelFormGrid">
                    <label className="channelControl">
                      <span>Channel Account</span>
                      <select value={selectedAccountId} onChange={(e) => setSelectedAccountId(Number(e.target.value))}>
                        <option value={0}>Select account</option>
                        {channelAccounts.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="channelControl">
                      <span>Hotel</span>
                      <select
                        value={propertyMapForm.hotelId}
                        onChange={(e) => setPropertyMapForm((current) => ({ ...current, hotelId: Number(e.target.value) }))}
                      >
                        <option value={0}>Select hotel</option>
                        {hotels.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}{item.code ? ` (${item.code})` : ''}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="channelControl">
                      <span>External Hotel ID</span>
                      <input
                        type="text"
                        value={propertyMapForm.externalHotelId}
                        placeholder="booking_property_id"
                        onChange={(e) => setPropertyMapForm((current) => ({ ...current, externalHotelId: e.target.value }))}
                      />
                    </label>
                  </div>

                  <div className="channelFormActions">
                    <button type="button" className="channelPrimaryButton" onClick={handleAddPropertyMap} disabled={savingPropertyMap}>
                      {savingPropertyMap ? 'Saving...' : 'Save Hotel Mapping'}
                    </button>
                  </div>
                </div>
              )}
              <div className="channelTableWrap">
                <table className="channelTable channelTableCompact">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Hotel ID</th>
                      <th>External Hotel ID</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPropertyMaps.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.hotelId}</td>
                        <td>{item.externalHotelId}</td>
                        <td>{formatDateTime(item.created)}</td>
                      </tr>
                    ))}
                    {filteredPropertyMaps.length === 0 && (
                      <tr>
                        <td colSpan={4} className="channelEmptyCell">
                          {i18n.t('bookingChannel.empty.propertyMaps', { defaultValue: 'No property maps found.' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {activeMappingTab === 'roomType' && (
            <div className="channelPanel">
              <div className="channelPanelHead">
                <div>
                    <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.mapping.kicker', { defaultValue: 'Mapping' })}</span>
                    <h2>{i18n.t('bookingChannel.mappingTabs.roomType', { defaultValue: 'Room Type Maps' })}</h2>
                </div>
                  <div className="channelBadge">{rowsLabel(filteredRoomTypeMaps.length)}</div>
              </div>
              <div className="channelTableWrap">
                <table className="channelTable channelTableCompact">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Hotel ID</th>
                      <th>Room Type ID</th>
                      <th>Room Type</th>
                      <th>External Room Type ID</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoomTypeMaps.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id || '-'}</td>
                        <td>{item.hotelId ?? '-'}</td>
                        <td>{item.roomTypeId}</td>
                        <td>
                          {item.roomTypeName || '-'}
                          {item.roomTypeCode ? ` (${item.roomTypeCode})` : ''}
                        </td>
                        <td>{item.externalRoomTypeId || '-'}</td>
                        <td>{formatDateTime(item.created)}</td>
                      </tr>
                    ))}
                    {filteredRoomTypeMaps.length === 0 && (
                      <tr>
                        <td colSpan={6} className="channelEmptyCell">
                            {i18n.t('bookingChannel.empty.roomTypeMaps', { defaultValue: 'No room types found for the linked hotel.' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {activeMappingTab === 'ratePlan' && (
            <div className="channelPanel">
              <div className="channelPanelHead">
                <div>
                    <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.mapping.kicker', { defaultValue: 'Mapping' })}</span>
                    <h2>{i18n.t('bookingChannel.mappingTabs.ratePlan', { defaultValue: 'Rate Plan Maps' })}</h2>
                </div>
                  <div className="channelBadge">{rowsLabel(filteredRatePlanMaps.length)}</div>
              </div>
              <div className="channelTableWrap">
                <table className="channelTable channelTableCompact">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Hotel ID</th>
                      <th>Rate Plan ID</th>
                      <th>Rate Plan</th>
                      <th>External Rate Plan ID</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRatePlanMaps.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id || '-'}</td>
                        <td>{item.hotelId ?? '-'}</td>
                        <td>{item.ratePlanId}</td>
                        <td>
                          {item.ratePlanName || '-'}
                          {item.ratePlanCode ? ` (${item.ratePlanCode})` : ''}
                        </td>
                        <td>{item.externalRatePlanId || '-'}</td>
                        <td>{formatDateTime(item.created)}</td>
                      </tr>
                    ))}
                    {filteredRatePlanMaps.length === 0 && (
                      <tr>
                        <td colSpan={6} className="channelEmptyCell">
                            {i18n.t('bookingChannel.empty.ratePlanMaps', { defaultValue: 'No rate plans found for the linked hotel.' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </section>
        )}

        {activeTab === 'reservations' && (
        <section className="channelPanel">
            <div className="channelPanelHead">
              <div>
                <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.reservation.kicker', { defaultValue: 'Reservation' })}</span>
                <h2>{i18n.t('bookingChannel.sections.reservation.title', { defaultValue: 'Channel Reservation Maps' })}</h2>
              </div>
              <div className="channelBadge">{rowsLabel(filteredReservationMaps.length)}</div>
            </div>
            <div className="channelTableWrap">
              <table className="channelTable">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>External Reservation</th>
                    <th>Reservation ID</th>
                    <th>Status</th>
                    <th>Last Sync</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReservationMaps.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.externalReservationId}</td>
                      <td>{item.reservationId}</td>
                      <td>
                        <span className={`channelPill status-${statusClassName(item.status)}`}>{item.status || '-'}</span>
                      </td>
                      <td>{formatDateTime(item.lastSyncAt)}</td>
                      <td>{formatDateTime(item.created)}</td>
                    </tr>
                  ))}
                  {filteredReservationMaps.length === 0 && (
                    <tr>
                      <td colSpan={6} className="channelEmptyCell">
                          {i18n.t('bookingChannel.empty.reservationMaps', { defaultValue: 'No reservation maps found.' })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === 'sync' && (
        <section className="channelPanel">
            <div className="channelPanelHead">
              <div>
                <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.sync.kicker', { defaultValue: 'Sync' })}</span>
                <h2>{i18n.t('bookingChannel.sections.sync.title', { defaultValue: 'Sync & Errors' })}</h2>
              </div>
            </div>
            {syncTabs}

            {activeSyncTab === 'jobs' && (
            <div className="channelPanel">
              <div className="channelPanelHead">
                <div>
                    <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.queue.kicker', { defaultValue: 'Queue' })}</span>
                    <h2>{i18n.t('bookingChannel.syncTabs.jobs', { defaultValue: 'Sync Jobs' })}</h2>
                </div>
                  <div className="channelBadge">{rowsLabel(filteredSyncJobs.length)}</div>
              </div>
              <div className="channelTableWrap">
                <table className="channelTable">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Retry</th>
                      <th>Payload</th>
                      <th>Response</th>
                      <th>Created</th>
                      <th>Processed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSyncJobs.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.jobType}</td>
                        <td>
                          <span className={`channelPill status-${statusClassName(item.status)}`}>{item.status || '-'}</span>
                        </td>
                        <td>{item.retryCount}</td>
                        <td className="channelMono">{item.payload || '-'}</td>
                        <td>{item.response || '-'}</td>
                        <td>{formatDateTime(item.createdAt)}</td>
                        <td>{formatDateTime(item.processedAt)}</td>
                      </tr>
                    ))}
                    {filteredSyncJobs.length === 0 && (
                      <tr>
                        <td colSpan={8} className="channelEmptyCell">
                            {i18n.t('bookingChannel.empty.syncJobs', { defaultValue: 'No sync jobs found.' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {activeSyncTab === 'errors' && (
            <div className="channelPanel">
              <div className="channelPanelHead">
                <div>
                    <span className="channelPanelKicker">{i18n.t('bookingChannel.sections.log.kicker', { defaultValue: 'Log' })}</span>
                    <h2>{i18n.t('bookingChannel.syncTabs.errors', { defaultValue: 'Sync Errors' })}</h2>
                </div>
                  <div className="channelBadge">{rowsLabel(filteredSyncErrors.length)}</div>
              </div>
              <div className="channelTableWrap">
                <table className="channelTable">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Entity Type</th>
                      <th>Entity ID</th>
                      <th>Error Message</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSyncErrors.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.entityType}</td>
                        <td>{item.entityId}</td>
                        <td>{item.errorMessage}</td>
                        <td>{formatDateTime(item.createdAt)}</td>
                      </tr>
                    ))}
                    {filteredSyncErrors.length === 0 && (
                      <tr>
                        <td colSpan={5} className="channelEmptyCell">
                            {i18n.t('bookingChannel.empty.syncErrors', { defaultValue: 'No sync errors found.' })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </section>
        )}
      </div>
    </BasicCli>
  );
};

export default BookingChannelPage;
